import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["map"]

  #abroad;
  #boundariesNetherlands;
  #map;
  #markerList;
  #clusterer;
  #markerIconUrl;
  #interactive = true;

  connect() {
    // Only initialize if the Maps API is fully loaded. `typeof google != "undefined"` is not
    // enough: with loading=async the `google` stub can exist before classes like LatLng are
    // ready. When the controller connects before the API is ready, the maps:onload data-action
    // triggers initializeMap once googleApiLoaded runs. (googleMapsReady is set there.)
    if (window.googleMapsReady) this.initializeMap();
  }

  initializeMap() {
    if (this.#map != undefined) return;

    this.#boundariesNetherlands = new google.maps.LatLngBounds(new google.maps.LatLng(50.5, 3.5), new google.maps.LatLng(53.5, 7.2));
    this.#map = this._initiateMap(this._options(this.mapTarget));
    this._dispatchOnload();

    if (this.mapTarget.dataset.markers) {
      const locations = JSON.parse(new DOMParser().parseFromString(this.mapTarget.dataset.markers, "text/html").documentElement.textContent);
      this._loadMarkers(locations);
    }
  }

  _initiateMap(options) {
    const map = new google.maps.Map(
      options.map,
      {
        center: {
          lat: options.lat,
          lng: options.lng
        },
        zoom: options.zoom,
        zoomControlOptions: options.zoomControlOptions,
        zoomControl: options.zoomControl,
        disableDoubleClickZoom: options.disableDoubleClickZoom,
        draggable: options.draggable,
        mapTypeControl: false,
        panControl: false,
        scrollwheel: options.interactive,
        streetViewControl: false,
        clickableIcons: options.interactive,
        keyboardShortcuts: options.interactive,
        gestureHandling: options.interactive ? 'auto' : 'none',
        // Without this, Google still draws its own buttons (full screen) on a
        // map that is only there as a picture.
        fullscreenControl: options.interactive,
        disableDefaultUI: !options.interactive,
        styles: this._setStyle(options.style)
      }
    )
    this.#abroad = options.abroad;
    this.#interactive = options.interactive;

    this._addMapListeners();

    return map;
  }

  _dispatchOnload() {
    this.element.dispatchEvent(new CustomEvent('map:onload', { bubbles: true, detail: { map: this.#map } }));
  }

  _addMapListeners() {
    this.element.addEventListener("map:onload_requested", () => {
      if (this.#map) this._dispatchOnload();
    });

    this.element.addEventListener("map:move", (e) => {
      this._move(e.detail.location, e.detail.zoom);
    });

    this.element.addEventListener("map:add_markers", (e) => {
      this._loadMarkers(e.detail.markerList);
    });

    this.element.addEventListener("map:reset", (e) => {
      this._autoCenter();

      e.stopPropagation;
      e.preventDefault;
    });

    this.element.addEventListener("map:filter_markers", (e) => {
      this._filterMarkers(e.detail.placeIds);

      e.stopPropagation;
      e.preventDefault;
    });
  }

  _filterMarkers(placeIds) {
    if (!this.#map || !this.#markerList) return;

    const allowed = new Set((placeIds || []).map(String));
    // Determine the visible markers before clearMarkers(): clearMarkers() sets
    // the map of every marker to null, so afterwards marker.getMap() would
    // return nothing.
    const visibleMarkers = this.#markerList.filter((marker) =>
      allowed.has(String(marker.place_id))
    );

    this.#markerList.forEach((marker) => {
      marker.setMap(allowed.has(String(marker.place_id)) ? this.#map : null);
    });

    if (this.#clusterer) {
      this.#clusterer.clearMarkers();
      this.#clusterer.addMarkers(visibleMarkers);
    }

    this._autoCenter(visibleMarkers);
  }

  _move(location, zoom) {
    this.#map.setCenter(location);
    this.#map.setZoom(zoom);
  }

  _addMarkers(map, locations) {
    return locations.map((location) => {
      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(location[1], location[2]),
        map: map,
        title: location[0],
        place_id: location[3],
        draggable: false,
        animation: google.maps.Animation.DROP,
        city: location[5]
      })

      this._setMarkerIconAndListener(marker, map)

      return marker
    })
  }

  _setMarkerIconAndListener(marker, map) {
    const size = this._markerIconSize();

    marker.setIcon({
      url: this._markerIconUrl(),
      size: new (google.maps.Size)(size, size),
      scaledSize: new (google.maps.Size)(size, size),
      // Keep the middle of the image on the location, whatever the size is.
      anchor: new (google.maps.Point)(size / 2, size / 2)
    })

    if (!this.#interactive) return;

    marker.addListener("click", () => {
      map.setZoom(19)
      map.setCenter(marker.getPosition())

      this.element.dispatchEvent(new CustomEvent('map:marker_selected', {
        bubbles: true,
        detail: { placeId: String(marker.place_id) }
      }));
    });
  }

  // The blue dot is drawn at 32px; an image of your own can be bigger through
  // data-marker-icon-size.
  _markerIconSize() {
    const size = parseInt(this.mapTarget.dataset.markerIconSize, 10);

    return size > 0 ? size : 32;
  }

  _markerIconUrl() {
    if (this.#markerIconUrl) return this.#markerIconUrl;

    // An image of your own (data-marker-icon) wins over the blue dot.
    const ownIcon = this.mapTarget.dataset.markerIcon;
    if (ownIcon) {
      this.#markerIconUrl = ownIcon;
      return this.#markerIconUrl;
    }

    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--color-blue').trim() || '#0073E6';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32"><defs><filter id="a" width="200%" height="200%" x="-50%" y="-50%" filterUnits="objectBoundingBox"><feOffset in="SourceAlpha" result="s1"/><feGaussianBlur in="s1" result="s2" stdDeviation="4"/><feColorMatrix in="s2" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.3 0"/></filter><circle id="b" cx="16" cy="16" r="8"/></defs><g fill="none" fill-rule="evenodd"><use href="#b" fill="#000" filter="url(#a)"/><use href="#b" fill="${color}"/></g></svg>`;

    this.#markerIconUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    return this.#markerIconUrl;
  }

  _loadMarkers(markerList) {
    this.#markerList = this._addMarkers(this.#map, markerList);

    if (typeof (MarkerClusterer) != "undefined") {
      this.#clusterer = new MarkerClusterer(this.#map, this.#markerList, {
        styles: Array(5).fill(MarkerClusterer.withDefaultStyle({
          url: "/assets/images/google-maps/marker-cluster.png",
          textColor: "white",
          width: 53,
          height: 53
        }))
      })
    }

    this._autoCenter();

    this.element.dispatchEvent(new CustomEvent('map:markers_updated', { bubbles: true, detail: { markerList: this.#markerList } }));
  }

  _autoCenter(markers = this.#markerList) {
    if (!this.#map || !markers) return;

    var bounds = new (google.maps.LatLngBounds)
    const relevantMarkers = markers.filter((marker) =>
      this.#abroad || this.#boundariesNetherlands.contains(marker.position)
    );
    if (relevantMarkers.length === 0) return;

    relevantMarkers.forEach((marker) => bounds.extend(marker.position));

    // Respect maxZoom during fitBounds already, so the map does not first zoom
    // in too far on few/single results only to jump back out afterwards.
    const maxZoom = parseInt(this.mapTarget.dataset.zoom);
    this.#map.setOptions({ maxZoom });
    this.#map.fitBounds(bounds);
    this.#map.panToBounds(bounds);

    // After centering, lift the limit again so manual zooming and clicking a
    // marker keep working.
    google.maps.event.addListenerOnce(this.#map, 'idle', () => {
      this.#map.setOptions({ maxZoom: null });
    });
  }

  _options(mapTarget) {
    // A map without interaction is a picture: no dragging, no zooming and no
    // controls. The marker keeps its title, so a screen reader still reads it.
    const interactive = mapTarget.dataset.interactive !== 'false';

    return {
      map: mapTarget,
      lat: parseFloat(mapTarget.dataset.latitude),
      lng: parseFloat(mapTarget.dataset.longtitude),
      zoom: parseFloat(mapTarget.dataset.zoom),
      style: mapTarget.dataset.style,
      interactive: interactive,
      draggable: interactive,
      zoomControl: interactive,
      disableDoubleClickZoom: !interactive,
      zoomControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM,
      },
      abroad: (mapTarget.dataset.abroad === 'true')
    }
  }

  _setStyle(type) {
    const landColor = '#e1f2ca';
    const urbanColor = '#f7f4ee';
    const roadColor = '#d2cfc8';
    const roadStroke = '#ffffff';
    const natureColor = '#dbf2c4';
    const waterColor = '#cae8f2';
    const labelColor = '#89847b';
    const adminLabelColor = '#89847b';

    const mapStylingDark = [
      {
        'elementType': 'geometry',
        'stylers': [{ 'color': '#233241' }]
      },
      {
        'elementType': 'labels',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'administrative',
        'elementType': 'geometry',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'poi',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'road',
        'elementType': 'geometry.fill',
        'stylers': [{ 'color': '#2c2c2c' }]
      },
      {
        'featureType': 'road',
        'elementType': 'labels.icon',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'road',
        'elementType': 'labels.text.fill',
        'stylers': [{ 'color': '#8a8a8a' }]
      },
      {
        'featureType': 'road.arterial',
        'elementType': 'geometry',
        'stylers': [{ 'color': '#37465A' }]
      },
      {
        'featureType': 'road.highway',
        'elementType': 'geometry',
        'stylers': [{ 'color': '#37465A' }]
      },
      {
        'featureType': 'road.highway.controlled_access',
        'elementType': 'geometry',
        'stylers': [{ 'color': '#37465A' }]
      },
      {
        'featureType': 'transit',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'water',
        'elementType': 'geometry',
        'stylers': [{ 'color': '#141E28' }]
      }
    ]

    const mapStylingLight = [
      {
        'featureType': 'all',
        'elementType': 'labels.text.fill',
        'stylers': [{ 'color': labelColor }]
      },
      {
        'featureType': 'all',
        'elementType': 'labels.text.stroke',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'administrative',
        'elementType': 'labels.text.fill',
        'stylers': [{ 'color': adminLabelColor }]
      },
      {
        'featureType': 'administrative.neighborhood',
        'elementType': 'labels',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'landscape',
        'elementType': 'geometry',
        'stylers': [{ 'color': landColor }]
      },
      {
        'featureType': 'landscape.man_made',
        'elementType': 'geometry.fill',
        'stylers': [{ 'color': urbanColor }]
      },
      {
        'featureType': 'landscape',
        'elementType': 'labels',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'poi',
        'elementType': 'all',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'poi.park',
        'elementType': 'geometry',
        'stylers': [{ 'color': natureColor }, { 'visibility': 'on' }]
      },
      {
        'featureType': 'road',
        'elementType': 'geometry.fill',
        'stylers': [{ 'color': roadColor }]
      },
      {
        'featureType': 'road',
        'elementType': 'geometry.stroke',
        'stylers': [{ 'color': roadStroke }]
      },
      {
        'featureType': 'road',
        'elementType': 'labels.icon',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'road.highway',
        'elementType': 'geometry.fill',
        'stylers': [{ 'color': roadColor }]
      },
      {
        'featureType': 'road.highway',
        'elementType': 'geometry.stroke',
        'stylers': [{ 'color': roadStroke }]
      },
      {
        'featureType': 'transit',
        'elementType': 'all',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'water',
        'elementType': 'geometry.fill',
        'stylers': [{ 'color': waterColor }]
      },
      {
        'featureType': 'water',
        'elementType': 'labels',
        'stylers': [{ 'visibility': 'off' }]
      }
    ]

    if (type == "dark") {
      return mapStylingDark
    }
    else {
      return mapStylingLight
    }
  }
}
